import { FC, useEffect } from 'react';
import { IonButton, IonContent, IonFooter, IonToolbar } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Modal } from '../components/Modal';
import { DevFlagsPopupViewModel } from './DevFlagsPopupViewModel';
import { DebugPageState } from './DebugPage';
import { DuoTriangleExclamationIcon } from '../theme/Icons';

const useStyles = createUseStyles({
    content: {
        padding: '16px 24px 24px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16
    },
    iconWrapper: {
        backgroundColor: 'rgba(var(--ion-color-warning-rgb), 0.15)',
        borderRadius: 8,
        padding: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    title: {
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        margin: 0
    },
    subtitle: {
        fontSize: 14,
        color: 'var(--ion-color-medium)',
        marginBottom: 20
    },
    settingsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    settingItem: {
        padding: '8px 0',
        borderBottom: '1px solid var(--ion-color-light)'
    },
    settingName: {
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--ion-color-dark)'
    },
    settingDescription: {
        fontSize: 12,
        color: 'var(--ion-color-medium)',
        marginTop: 2
    },
    footerToolbar: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 24px 24px'
    }
});

export const DevFlagsPopup: FC = () => {
    const classes = useStyles();
    const vm = useInstance(DevFlagsPopupViewModel);
    const debugPageState = useInstance(DebugPageState);
    useSubscription(() => vm);

    // Check if popup should show after a brief delay (app fully loaded)
    useEffect(() => {
        const timer = setTimeout(() => {
            vm.checkAndShow();
        }, 1000);
        return () => clearTimeout(timer);
    }, [vm]);

    const enabledFlags = vm.getEnabledDevFlags();
    const totalFlags = enabledFlags.length;

    const handleOpenSettings = () => {
        vm.close();
        debugPageState.showSettings();
    };

    return (
        <Modal
            title=""
            data-id="dev-flags-popup"
            isOpen={vm.isOpen()}
            closeHandler={vm.close}
            desktopMinHeight={450}
            hideCloseButton
        >
            <IonContent>
                <div className={classes.content}>
                    <div className={classes.header}>
                        <div className={classes.iconWrapper}>
                            <DuoTriangleExclamationIcon color="warning" size="large" />
                        </div>
                        <h2 className={classes.title}>
                            {totalFlags} Dev {totalFlags === 1 ? 'Flag' : 'Flags'} Enabled
                        </h2>
                    </div>

                    <p className={classes.subtitle}>
                        The following developer settings are currently active and will affect app behavior:
                    </p>

                    <ul className={classes.settingsList}>
                        {enabledFlags.map(setting => (
                            <li key={setting.key} className={classes.settingItem}>
                                <div className={classes.settingName}>{setting.name}</div>
                                <div className={classes.settingDescription}>{setting.description}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            </IonContent>

            <IonFooter>
                <IonToolbar className={classes.footerToolbar}>
                    <IonButton
                        expand="block"
                        onClick={vm.close}
                        data-id="dev-flags-got-it"
                    >
                        Got It
                    </IonButton>
                    <IonButton
                        expand="block"
                        fill="clear"
                        onClick={handleOpenSettings}
                        data-id="dev-flags-open-settings"
                    >
                        Open Debug Settings
                    </IonButton>
                </IonToolbar>
            </IonFooter>
        </Modal>
    );
};
